"use client";

import type { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { type JSX } from "react";
import { DefaultTheme } from "styled-components";

import { ColorOrKey } from "../Color";

import {
  Position,
  TooltipColors,
  TooltipVariant,
  _buildTooltip,
  _buildTinyTooltip,
} from "./common";

interface ICommonTooltipProps {
  position?: Position;
  /**
   * Used to shift the pointer relative to the tooltip content box by the specified number.
   *
   * By default offset is 0, and the pointer is centered on the tooltip content box
   *
   * A negative offset shifts the pointer left (in a "top" or "bottom" positioning)
   * or up (in a "left" or "right" positioning)
   *
   * A positive offset shifts the pointer right (in a "top" or "bottom" positioning)
   * or down (in a "left" or "right" positioning)
   */
  pointerOffset?: number;
  /** Allows the tooltip to pop out of it's parent container even if it scrolls.
   * To use this, a parent outside the scrolling div needs to use position: relative
   * and then you can set the top/left/bottom/right properties relative to the parent.
   * If you want the tooltip to appear next to the tooltip icon, you'll need to do some
   * math using refs. Check out Tooltip.stories.tsx for an example.
   */
  popout?: {
    top?: string;
    left?: string;
    bottom?: string;
    right?: string;
  };
  innerClassName?: string;
  className?: string;
  iconColor?: ColorOrKey;
  /**
   * @deprecated use targetNode instead
   * @trackcomponent
   */
  icon?: React.ReactNode;
  targetNode?: React.ReactNode;
  variant?: TooltipVariant;
  /**
   * @deprecated use variant instead
   * @trackcomponent
   */
  primary?: boolean;
  theme?: DefaultTheme;
  colors?: TooltipColors;
  children?: React.ReactNode;
  isOpen?: boolean;
  innerRef?: React.Ref<HTMLSpanElement>;
  hidden?: boolean;
  "data-test-id"?: string;
  onIndexChange?: (index: number) => void;
  dismissable?: boolean;
  /**
   * Defaults to false.
   * If true, the tooltip will not be allowed to go outside the viewport
   * horizontally (which otherwise causes horizontal scrolling)
   * If set to true and an offset was provided, this property will take precedence.
   */
  keepInsideViewport?: boolean;
}
export interface ITinyTooltipProps extends ICommonTooltipProps {
  text?: string | React.ReactElement<typeof StrictLocalMsg>;
  tiny: boolean;
}
export interface ITooltipProps extends ICommonTooltipProps {
  width?: number;
  /**
   * Each element in `bodies` or the array of `title` and `body` will add
   * an index to the tooltip that can be navigated backwards and forwards
   * through. If a single title is provided for an array of `bodies`, it will
   * be used for every index. If an array of `title` and `body` is passed,
   * each `title` and `body` will be used for its corresponding index.
   */
  contents?:
    | {
        title?: string;
        bodies?: string[];
      }
    | {
        title?: string;
        body?: string;
      }[];
  action?: React.ReactNode;
}

/**
 * @deprecated use `Tooltip` from `@faire/slate/components/tooltip` or `Popover` from `@faire/slate/components/popover` - since 2025-04-05
 *
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/component/tooltip)
 */
export function Tooltip(tinyTooltipProps: ITinyTooltipProps): JSX.Element;
export function Tooltip(tooltipProps: ITooltipProps): JSX.Element;

export function Tooltip(props: ITooltipProps | ITinyTooltipProps) {
  const tinyTooltipProps = props as ITinyTooltipProps;

  if (tinyTooltipProps.tiny) {
    return _buildTinyTooltip(props as ITinyTooltipProps);
  }
  return _buildTooltip(props, false);
}
